import { isGeomKind, type GeomKind } from "@bezpart-board/shared";
import { isVolumeGeomKind } from "./volumeShapes";

/**
 * Точка в центре плоской фигуры: центр окружности, точка пересечения
 * диагоналей, центр тяжести треугольника. У объёмных фигур
 * («учебная проекция» шара, конуса, призмы) центра на плоскости нет —
 * там точка была бы неправдой, поэтому их исключаем.
 */

type CenterShape = {
  type: string;
  kind?: unknown;
};

export function shapeSupportsCenter(obj: CenterShape): boolean {
  if (obj.type === "geom") {
    if (!isGeomKind(obj.kind)) return false;
    return !isVolumeGeomKind(obj.kind as GeomKind);
  }
  return (
    obj.type === "rect" ||
    obj.type === "ellipse" ||
    obj.type === "triangle" ||
    obj.type === "diamond" ||
    obj.type === "star"
  );
}

/**
 * У треугольников центр — точка пересечения медиан, а не середина
 * описывающего прямоугольника: иначе на уроке геометрии точка стояла бы
 * заметно не там, где ей положено.
 */
export function shapeCenterPoint(
  obj: CenterShape,
  w: number,
  h: number
): { x: number; y: number } {
  if (obj.type === "triangle") {
    // вершины (w/2, 0), (w, h), (0, h)
    return { x: w / 2, y: (2 * h) / 3 };
  }
  if (obj.type === "geom" && obj.kind === "right_triangle") {
    // вершины (0, 0), (w, h), (0, h)
    return { x: w / 3, y: (2 * h) / 3 };
  }
  if (obj.type === "geom" && obj.kind === "left_triangle") {
    // вершины (w, 0), (w, h), (0, h)
    return { x: (2 * w) / 3, y: (2 * h) / 3 };
  }
  return { x: w / 2, y: h / 2 };
}

/** Радиус точки: заметна на тонком контуре, не разрастается на толстом. */
export function shapeCenterRadius(strokeWidth: number): number {
  const base = Number.isFinite(strokeWidth) && strokeWidth > 0 ? strokeWidth : 1;
  return Math.max(2.5, Math.min(5, base * 1.6));
}
